import { create } from 'zustand';
import {
  api,
  ApiError,
  setAccessChangedHandler,
  setAccessToken,
  setKnownAccessHash,
  setSessionLostHandler,
  type CurrentUser,
  type LoginResponse,
  type TwoStepRequired,
} from '@/api/client';
import { setStorageUser } from '@/components/desktop/types';
import { onWindowMessage, tellOtherWindows } from '@/stores/channel';
import { useConnection } from '@/stores/connection';
import { useCompany } from '@/stores/company';
import { useFarmScope } from '@/stores/farmScope';
import { clearLock } from '@/stores/lock';
import { usePreferences } from '@/stores/preferences';
import { sweepOldDrafts } from '@/stores/drafts';
import { discardUnsavedWork } from '@/stores/unsaved';
import { closeCopy, openCopyOffline, openCopyOnline, rememberInCopy } from '@/offline/session';

interface AuthState {
  user: CurrentUser | null;
  /** Distinguishes "not signed in" from "still checking", so a refresh does
   *  not flash the login screen at an already-authenticated user. */
  status: 'checking' | 'authenticated' | 'anonymous';
  error: string | null;
  /** Shown once on the sign-in screen, e.g. after a reset signed everyone out. */
  notice: string | null;
  signingIn: boolean;
  /**
   * True when the person signed in with no connection, against the copy of
   * their work this PC keeps (.docs/023). The server has not confirmed them
   * yet; it is asked the moment the connection is back.
   */
  offlineSession: boolean;
  /**
   * Set when the password was right and two-step sign-in is on: the code
   * from the phone is asked for next, sent with this challenge.
   */
  twoStepChallenge: string | null;

  signIn: (identifier: string, password: string) => Promise<void>;
  /** The second half of signing in: the code from the phone, or a backup code. */
  finishTwoStep: (code: string) => Promise<void>;
  /** Back to the user ID and password. */
  cancelTwoStep: () => void;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
  /** Re-reads the signed-in user, e.g. once a required password change is done. */
  refreshUser: () => Promise<void>;
  /** Ends the session here and in every window, with a note for the sign-in screen. */
  endSession: (notice: string) => void;
  /**
   * True when the user holds the given permission, e.g. 'inv.price.read'.
   * `'@helper'` is not a permission but the one thing an access helper has
   * instead of one: a delegation in force (.docs/027).
   */
  can: (permission: string) => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  status: 'checking',
  error: null,
  notice: null,
  signingIn: false,
  offlineSession: false,
  twoStepChallenge: null,

  signIn: async (identifier, password) => {
    set({ signingIn: true, error: null, notice: null });
    // Kept only until the server says yes, to wrap this person's copy for the
    // password they just typed (see signedIn). Never stored as itself.
    typedPassword = password;
    try {
      const res = await api.post<LoginResponse | TwoStepRequired>('/api/v1/auth/login', {
        identifier,
        password,
      });
      if ('twoStepRequired' in res && res.twoStepRequired) {
        set({ twoStepChallenge: res.challenge, signingIn: false, status: 'anonymous' });
        return;
      }
      signedIn(res as LoginResponse);
    } catch (e) {
      typedPassword = null;
      if (e instanceof ApiError && e.status === 0) {
        // No connection: the copy this PC keeps decides instead, with the same
        // password. It says plainly when it cannot.
        try {
          const user = await openCopyOffline(identifier, password);
          startSession(user);
          set({ user, status: 'authenticated', offlineSession: true, signingIn: false, error: null });
        } catch (offline) {
          set({
            error: offline instanceof Error ? offline.message : 'Sign-in failed',
            signingIn: false,
            status: 'anonymous',
          });
        }
        return;
      }
      set({
        error: e instanceof Error ? e.message : 'Sign-in failed',
        signingIn: false,
        status: 'anonymous',
      });
    }
  },

  finishTwoStep: async (code) => {
    const challenge = get().twoStepChallenge;
    if (!challenge) return;
    set({ signingIn: true, error: null });
    try {
      const res = await api.post<LoginResponse>('/api/v1/auth/two-step', { challenge, code });
      signedIn(res);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Sign-in failed';
      // A challenge that ran out cannot be used again: back to the password.
      const expired = /took too long|account is locked/i.test(message);
      set({
        error: message,
        signingIn: false,
        ...(expired ? { twoStepChallenge: null } : {}),
      });
    }
  },

  cancelTwoStep: () => {
    typedPassword = null;
    set({ twoStepChallenge: null, error: null });
  },

  signOut: async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Even if the server call fails, the local session must end.
    }
    endSession();
    set({ user: null, status: 'anonymous', error: null });
    tellOtherWindows({ type: 'signed-out' });
  },

  restore: async () => {
    const user = await api.restoreSession();
    if (user) {
      startSession(user);
      set({ user, status: 'authenticated', offlineSession: false });
      void openCopy(user, null);
      return;
    }

    // The session could not be confirmed. With no connection the sign-in
    // screen offers to sign in against the copy this PC keeps, with the
    // password. It used to open as whoever was here last with no password at
    // all; once work can be saved offline, that would let anybody at the PC
    // save work in somebody else's name (.docs/023).
    set({ user: null, status: 'anonymous', offlineSession: false });
  },

  refreshUser: async () => {
    const user = (await api.get('/api/v1/auth/me')) as CurrentUser;
    setKnownAccessHash(user.accessHash);
    rememberInCopy(user);
    set({ user });
  },

  endSession: (notice) => {
    endSession();
    set({ user: null, status: 'anonymous', error: null, notice });
    tellOtherWindows({ type: 'signed-out' });
  },

  can: (permission) =>
    permission === '@helper'
      ? (get().user?.isAccessHelper ?? false)
      : (get().user?.permissions.includes(permission) ?? false),
}));

function signedIn(res: LoginResponse): void {
  setAccessToken(res.accessToken);
  startSession(res.user);
  void openCopy(res.user, typedPassword);
  typedPassword = null;
  useAuth.setState({
    user: res.user,
    status: 'authenticated',
    signingIn: false,
    offlineSession: false,
    twoStepChallenge: null,
  });
  tellOtherWindows({ type: 'signed-in' });
}

/** The password just typed, held only until the server answers. */
let typedPassword: string | null = null;

/**
 * Who was last signed in here used to be kept in plain `localStorage`, with
 * their permissions, so the app could open offline with no password. The
 * encrypted copy replaced it (.docs/023); what an older build left is removed.
 */
try {
  localStorage.removeItem('poultry.lastUser');
} catch {
  // Private mode: there was nothing stored.
}

/**
 * Opens the person's copy for offline use. A PC an administrator has revoked
 * has its copies wiped, and the session ends with the server's words.
 */
async function openCopy(user: CurrentUser, password: string | null): Promise<void> {
  const { revoked } = await openCopyOnline(user, password);
  if (revoked) useAuth.getState().endSession(revoked);
}

/**
 * Everything that belongs to the person rather than the window: whose
 * remembered layout to read, their chosen farm, the company's formats.
 * Runs before the user is set, so screens mount with the right keys.
 */
function startSession(user: CurrentUser): void {
  setStorageUser(user.id);
  setKnownAccessHash(user.accessHash);
  // Half-forms nobody came back to, so a week of them cannot fill up storage.
  sweepOldDrafts();
  useFarmScope.getState().reload();
  void useCompany.getState().load();
}

function endSession(): void {
  setAccessToken(null);
  setKnownAccessHash(null);
  // Closed, not deleted: it may hold work not yet sent, and it is sealed.
  closeCopy();
  // Whatever was typed is gone with the session; the guard must not keep
  // asking about forms that no longer exist.
  discardUnsavedWork();
  usePreferences.getState().clear();
  clearLock();
  setStorageUser(null);
  useFarmScope.getState().reload();
}

/**
 * Signing out in one window signs out every window: otherwise the others
 * keep working on their access token for up to 15 minutes. Signing in on
 * the login screen of one window brings the others back too.
 */
onWindowMessage((m) => {
  const { status, restore } = useAuth.getState();
  if (m.type === 'signed-out' && status === 'authenticated') {
    endSession();
    useAuth.setState({ user: null, status: 'anonymous', error: null });
  } else if (m.type === 'signed-in' && status === 'anonymous') {
    void restore();
  }
});

/**
 * An offline session is a guess held only until the server can be asked. The
 * moment the connection is back it is confirmed — or the person is put back at
 * the sign-in screen, which is the honest outcome if the session really ended.
 */
useConnection.subscribe((state, previous) => {
  if (state.online && !previous.online && useAuth.getState().offlineSession) {
    void useAuth.getState().restore();
  }
});

setSessionLostHandler((reason) => {
  endSession();
  useAuth.setState({
    user: null,
    status: 'anonymous',
    // Whatever the server said why (a password change, a reset, an
    // administrator's action) is worth more on the sign-in screen than the
    // generic "you were signed out" the error field would otherwise show.
    notice: reason ?? null,
  });
});

/**
 * Someone changed this person's roles, permissions or farms: read them again,
 * once, however many answers arrive carrying the new hash at the same moment.
 */
let rereading: Promise<void> | null = null;
setAccessChangedHandler(() => {
  if (useAuth.getState().status !== 'authenticated') return;
  rereading ??= useAuth
    .getState()
    .refreshUser()
    .catch(() => undefined)
    .finally(() => {
      rereading = null;
    });
});
